import React from 'react';

import { Box, Typography } from '@mui/material';
import NavNeededCountBadge from '@sharedCR/common/NavNeededCountBadge';
import { ACTION_CELL_WIDTH_WITH_BADGES } from '@sharedCR/common/SimpleTable/SimpleTable.constants';
import StatusIcon from '@sharedCR/common/StatusIcon';
import TooltipWrapper from 'app/shared/components/common/TooltipWrapper';
import { ENTITY_REFERENCE_TYPE } from 'app/shared/components/TeamChat/TeamChat.entityReferences.utils';
import TeamChatReferenceBadge from 'app/shared/components/TeamChat/TeamChatReferenceBadge';

import { typeOfCreditMap } from 'constants/options';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { StatusEnum } from 'constants/status';
import { getConditionalObject } from 'utils/data';
import { formatNumberToCurrencyString } from 'utils/number';

import { FINANCIAL_INFO_VIEWS_PATHS } from '../../FinancialInfo.constants';
import ActionButton from '../ActionButton';
import BorrowersNames from '../BorrowersNames';
import { actionCellStyles, headCellLabelStyles } from '../FinancialInfoBody.styles';

import { currencyStyles, wrappedStyles } from './Liabilities.styles';
import { getLiabilityPropertyTooltip } from './Liabilities.utils';

const renderPropertyTooltipCell = (text, row, propertyAddressById) => {
  const propertyTooltip = getLiabilityPropertyTooltip(row, propertyAddressById);

  return (
    <TooltipWrapper title={propertyTooltip ?? ''}>
      <Typography data-truncated={!propertyTooltip} sx={wrappedStyles} variant="regular14">
        {text}
      </Typography>
    </TooltipWrapper>
  );
};

export const getConfig = ({
  isExpanded,
  onRemove,
  chatTargets = null,
  neededCountById = {},
  propertyAddressById = {},
}) => ({
  ownersInfo: {
    label: 'Borrower',
    width: isExpanded ? '21.2%' : '39%',
    colSpan: ({ ownersInfo }) => ownersInfo?.colSpan,
    customStyles: ({ ownersInfo }) => ownersInfo?.customStyles,
    content: ({
      ownersInfo,
      modifiedFields: rowModifiedFields,
      pendingChangesActionType: rowPendingChangesActionType,
    }) =>
      ownersInfo?.content ?? (
        <BorrowersNames
          ownersInfo={ownersInfo}
          rowModifiedFields={rowModifiedFields}
          rowPendingChangesActionType={rowPendingChangesActionType}
        />
      ),
    fieldName: 'ownersFullNames',
  },
  liabilityAccountType: {
    label: 'Type',
    width: isExpanded ? '10.5%' : '23.4%',
    content: row =>
      renderPropertyTooltipCell(
        typeOfCreditMap[row.liabilityAccountType || row.liabilityExpenseType]?.name ?? '-',
        row,
        propertyAddressById,
      ),
  },
  ...getConditionalObject(isExpanded, {
    creditorName: {
      label: 'Creditor',
      width: '10.5%',
      content: row => renderPropertyTooltipCell(row.creditorName || '-', row, propertyAddressById),
    },
    unpaidBalance: {
      label: (
        <Typography data-truncated sx={headCellLabelStyles} variant="regular14">
          Unpaid Balance
        </Typography>
      ),
      width: '10.5%',
      align: 'right',
      content: ({ unpaidBalance }) => (
        <Typography data-truncated className="truncate" sx={currencyStyles} variant="regular14">
          {formatNumberToCurrencyString(unpaidBalance)}
        </Typography>
      ),
    },
  }),
  monthlyPayment: {
    label: 'Payment/Mo',
    align: 'right',
    width: isExpanded ? '10.5%' : '25.4%',
    content: ({ monthlyPayment, remainingMonths }) => {
      const payment = formatNumberToCurrencyString(Number(monthlyPayment));

      return (
        <Typography data-truncated className="truncate" sx={currencyStyles} variant="regular14">
          {remainingMonths ? `${payment}/${remainingMonths}` : payment}
        </Typography>
      );
    },
  },
  ...getConditionalObject(isExpanded, {
    remainingPayments: {
      label: (
        <Typography data-truncated sx={headCellLabelStyles} variant="regular14">
          Remaining Payments
        </Typography>
      ),
      width: '10.5%',
      align: 'center',
      content: ({ remainingPayments }) => (
        <Typography data-truncated sx={wrappedStyles} variant="regular14">
          {remainingPayments || '-'}
        </Typography>
      ),
    },
    payoffRequired: {
      label: (
        <Typography data-truncated sx={headCellLabelStyles} variant="regular14">
          Payoff Required
        </Typography>
      ),
      width: '10.5%',
      align: 'center',
      content: ({ payoffRequired }) => (
        <StatusIcon status={payoffRequired ? StatusEnum.SUCCESS : StatusEnum.ERROR} />
      ),
    },
    exclude: {
      label: 'Exclude',
      width: '10.5%',
      align: 'center',
      content: ({ exclude }) => (
        <StatusIcon status={exclude ? StatusEnum.SUCCESS : StatusEnum.ERROR} />
      ),
    },
  }),
  action: {
    label: 'Action',
    width: ACTION_CELL_WIDTH_WITH_BADGES,
    content: ({ id, pendingChangesActionType }) => {
      const isAdded = pendingChangesActionType === PendingChangesActionType.ADDED;

      return (
        <Box sx={actionCellStyles}>
          <NavNeededCountBadge count={neededCountById[id] ?? 0} />
          <TeamChatReferenceBadge
            chatId={chatTargets?.internalChatId}
            entityId={id}
            entityType={ENTITY_REFERENCE_TYPE.LIABILITY}
          />
          <ActionButton
            chatTargets={chatTargets}
            entityType={ENTITY_REFERENCE_TYPE.LIABILITY}
            id={id}
            isHighlighted={isAdded}
            removeText="Delete Liability"
            tablePath={FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES}
            onRemove={onRemove}
          />
        </Box>
      );
    },
    align: 'center',
  },
});
